// ── @matix/utils/arabic ────────────────────────────────────────────────────
//
// Arabic text helpers, ported from warsha CRMPage.jsx normalizeAr().
// Used wherever we compare or search Arabic strings on the client.
//
// The matching backend helper lives in pos-api at src/utils/arabic.ts.

/**
 * Normalize Arabic text for comparison/search:
 * - removes diacritics (tashkeel)
 * - removes tatweel (kashida)
 * - unifies alef variants (إ أ آ → ا)
 * - unifies yeh (ى → ي)
 * - unifies teh marbuta (ة → ه)
 * - lowercases ASCII portions
 * - collapses whitespace
 */
export function normalizeAr(input: string | null | undefined): string {
  if (input == null) return '';
  return input
    .toString()
    .replace(/[\u064B-\u0652\u0670]/g, '') // tashkeel
    .replace(/\u0640/g, '') // tatweel
    .replace(/[\u0622\u0623\u0625]/g, '\u0627') // alef
    .replace(/\u0649/g, '\u064A') // alef maksura → yeh
    .replace(/\u0629/g, '\u0647') // teh marbuta → heh
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Loose equality check between two Arabic strings using normalized form.
 */
export function arabicEquals(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeAr(a) === normalizeAr(b);
}

/**
 * Substring search using normalized form. Returns true if `needle` appears in `haystack`.
 */
export function arabicIncludes(
  haystack: string | null | undefined,
  needle: string | null | undefined,
): boolean {
  const n = normalizeAr(needle);
  if (!n) return true;
  return normalizeAr(haystack).includes(n);
}

/**
 * Returns true if a string contains any Arabic characters.
 */
export function isArabic(input: string | null | undefined): boolean {
  if (!input) return false;
  return /[\u0600-\u06FF]/.test(input);
}
